import { useMemo, useRef } from 'react'

import {
  ScrollFade,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  TruncatedText,
  useResizableColumns,
} from '@/components/ui'
import {
  AccountTypeBadge,
  type AccountTypeBadgeVariant,
  type DirectoryAccount,
  type Transaction,
} from '@/modules/projects'
import { cn, numberToCurrency } from '@/utils'
import { SortableHead, useSort } from '../sort'
import { directoryByLast4, foldAccounts, withDirectoryLabels } from './accounts'

const headers = [
  ['Acct. #', 'number', 'w-[105px]'],
  ['Acct. Owner', 'owner'],
  ['Institution', 'institution'],
  ['Acct. Type', 'type', 'w-[110px]'],
  ['Inflows', 'inflows', 'text-right w-[120px]'],
  ['Outflows', 'outflows', 'text-right w-[120px]'],
  ['Txn Count', 'count', 'text-right w-[110px]'],
]

const columnKeys = headers.map(([, sortKey]) => sortKey as string)

interface AccountTableProps {
  transactions: Transaction[]
  directoryAccounts: DirectoryAccount[]
}

export const AccountTable = ({
  transactions,
  directoryAccounts,
}: AccountTableProps) => {
  const { sort, setSort, sorter } = useSort({
    key: 'inflows',
    direction: 'desc',
  })
  const headerRowRef = useRef<HTMLTableRowElement>(null)
  const { widths, totalWidth, resizingKey, startResize } = useResizableColumns(
    headerRowRef,
    columnKeys,
  )

  const accountStats = useMemo(() => {
    if (!transactions || transactions.length === 0) return []

    const directory = directoryByLast4(directoryAccounts)

    return foldAccounts(transactions)
      .map((account) => withDirectoryLabels(account, directory))
      .sort(sorter)
  }, [transactions, directoryAccounts, sort])

  if (accountStats.length === 0) return

  return (
    <ScrollFade containerClassName="w-full border-t" className="max-h-[400px]">
      {/* min-w floor = sum of fixed column widths (565px) + readable minimums
          for the two flexible owner/institution columns; below it the table
          scrolls instead of collapsing those columns. Update when columns change. */}
      <table
        className={cn(
          'caption-bottom text-sm table-fixed',
          !widths && 'w-full min-w-[795px]',
        )}
        style={widths ? { width: totalWidth ?? undefined } : undefined}
      >
        {widths && (
          <colgroup>
            {columnKeys.map((key) => (
              <col key={key} style={{ width: widths[key] }} />
            ))}
          </colgroup>
        )}
        <TableHeader className="sticky top-0 z-10 bg-blue-1">
          <TableRow ref={headerRowRef} className="bg-blue-1">
            {headers.map(([label, sortKey, className]) => (
              <SortableHead
                key={sortKey}
                label={label}
                sortKey={sortKey as string}
                sort={sort}
                onClick={setSort}
                className={cn(className)}
                onResizeStart={(event) => startResize(sortKey as string, event)}
                isResizing={resizingKey === sortKey}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {accountStats.map((account) => (
            <TableRow key={account.number}>
              <TableCell className="text-blue-6">
                <TruncatedText text={account.number} />
              </TableCell>
              <TableCell className="text-blue-6">
                <TruncatedText text={account.owner || '--'} />
              </TableCell>
              <TableCell className="text-blue-6">
                <TruncatedText text={account.institution || '--'} />
              </TableCell>
              <TableCell className="text-blue-6">
                <AccountTypeBadge
                  type={account.type as AccountTypeBadgeVariant}
                />
              </TableCell>
              <TableCell className="text-right text-emerald-500 whitespace-nowrap">
                {numberToCurrency(account.inflows)}
              </TableCell>
              <TableCell className="text-right text-red-600 whitespace-nowrap">
                {numberToCurrency(account.outflows)}
              </TableCell>
              <TableCell className="text-right text-blue-6">
                {account.count}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </table>
    </ScrollFade>
  )
}
